/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Virtual interaction sites for the crystal MD engine. A virtual site
 * carries a charge (and one day, an LJ center) at a position that is a
 * fixed linear combination of several real atoms' positions. The
 * canonical use is the 4-site water model family (TIP4P, TIP4P/Ice,
 * TIP4P/2005), where the oxygen's negative charge is offset from the
 * O atom toward the hydrogens to better reproduce the H-bond geometry
 * and the ice phase diagram.
 *
 * Implementation choice: linear combination of parent positions with
 * fixed weights summing to 1. This is the "linear M-site" formulation
 * (Berendsen 1980s, Jorgensen 1980s). The d_OM distance only stays at
 * its nominal value when the parent geometry is exactly rigid; for a
 * flexible-bond variant the d_OM fluctuates slightly with the H-O-H
 * angle and O-H lengths, which is acceptable because (a) the bonded
 * forces hold the geometry tight anyway and (b) the alternative
 * (out-of-plane projection with chain-rule force decomposition) is
 * substantially more complex without changing thermodynamics
 * appreciably at the temperatures we care about.
 *
 * Force decomposition: since M = Σ wₖ Pₖ, the chain rule gives
 *   ∂M/∂Pₖ = wₖ · I
 * so a force F applied to M decomposes to each parent as wₖ · F. We
 * accumulate that into the parent's `force` field divided by parent
 * mass — same acceleration convention CrystalPairInteractions uses
 * for real atoms.
 */
import { Vector3 } from 'three';
import { Atom } from '../Atom.ts';

export class VirtualSite {
  /**
   * Live position — recomputed each step from `updatePosition()`. The
   * pair kernel reads this directly; never mutates it.
   */
  position: Vector3 = new Vector3();
  /** Point charge in units of e. */
  charge: number;
  /** Real atoms whose positions define this site. */
  parents: Atom[];
  /** Linear weights, same length as `parents`, must sum to 1. */
  weights: number[];
  /**
   * Same intramolecular-exclusion tag the parent atoms carry — the
   * pair kernel skips Coulomb between any entities (real or virtual)
   * sharing this label. Set from `parents[0].moleculeName` so the
   * M-site always inherits its host molecule's id.
   */
  moleculeName?: string;

  constructor(parents: Atom[], weights: number[], charge: number) {
    if (parents.length !== weights.length) {
      throw new Error('VirtualSite: parents and weights must be the same length');
    }
    this.parents = parents;
    this.weights = weights;
    this.charge = charge;
    this.moleculeName = parents[0]?.moleculeName;
    this.updatePosition();
  }

  /**
   * Recompute `position` from the current parent positions. Call once
   * per integrator step, after the predict() pass and before the
   * force calculation.
   */
  updatePosition() {
    this.position.set(0, 0, 0);
    for (let k = 0; k < this.parents.length; k++) {
      const w = this.weights[k];
      this.position.x += w * this.parents[k].position.x;
      this.position.y += w * this.parents[k].position.y;
      this.position.z += w * this.parents[k].position.z;
    }
  }

  /**
   * Chain-rule velocity: since `position = Σₖ wₖ · parents[k].position`
   * is a linear combination of parent positions with fixed weights, the
   * time derivative is the same linear combination of parent velocities.
   * Exact — no numerical differentiation noise. Used by the IR-spectrum
   * dipole-current sampler so the M-site charge in TIP4P-style rigid
   * water models contributes correctly to J(t) = Σᵢ qᵢ vᵢ(t).
   *
   * Writes into `out` rather than allocating, so the hot integrator
   * loop can keep a single scratch Vector3 across all virtual sites.
   */
  computeVelocity(out: Vector3) {
    out.set(0, 0, 0);
    for (let k = 0; k < this.parents.length; k++) {
      const w = this.weights[k];
      const v = this.parents[k].velocity;
      if (!v) continue;
      out.x += w * v.x;
      out.y += w * v.y;
      out.z += w * v.z;
    }
  }

  /**
   * Distribute a Cartesian force `(fx, fy, fz)` (already pre-
   * multiplied by GF_CONVERSION_CONSTANT — i.e. mass-weighted
   * acceleration units) to the parent atoms via the linear weights.
   * Each parent's `force` field accumulates `wₖ · F / mₖ` so the
   * result is acceleration, matching the rest of the kernel's
   * convention.
   */
  applyForce(fx: number, fy: number, fz: number) {
    for (let k = 0; k < this.parents.length; k++) {
      const w = this.weights[k];
      const m = this.parents[k].mass;
      const f = this.parents[k].force;
      if (!f) continue;
      f.x += (fx * w) / m;
      f.y += (fy * w) / m;
      f.z += (fz * w) / m;
    }
  }
}
